import { 
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  Drawer,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState, useRef } from "react";
import MuiAutocomplete from "../elements/Mui/mui_Autocomplete";
import MuiInput from "../elements/Mui/mui_input";
import MuiDatePicker from "../elements/Mui/mui_date_picker";
import MuiInputAmount from "../elements/Mui/mui_input_amount";
import CustomAxios from "../utils/CustomAxios";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

export function CustomDeductionForm({ bankList, onBack }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [memberCount, setMemberCount] = useState(0);
  const [memberIds, setMemberIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchMemberId, setSearchMemberId] = useState("");

  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [dialogType, setDialogType] = useState("success");
  // Form fields
  const [narration, setNarration] = useState("");
  const [bank, setBank] = useState("");
  const [chequeno, setChequeno] = useState("");
  const [deductionAmount, setDeductionAmount] = useState(13);
  const [transdate, setTransdate] = useState(null);
  const [deductFrom, setDeductFrom] = useState("Subscription");

  const deductionAmountRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileSelection = (file) => {
    setFileError("");

    const isValidFile =
      file.name.endsWith(".csv") ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");

    if (!isValidFile) {
      setFileError(
        "Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file"
      );
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError("File size should not exceed 10MB");
      return;
    }

    setUploadedFile(file);
    setIsFileUploaded(true);
    setLoading(true);
    setFileError("");

    readFileContent(file);
  };

  const readFileContent = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        let data = [];
        let memberIdsList = [];

        const processRows = (rows) => {
          // Detect header row containing SN and MNo
          let headerRowIndex = -1;
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!Array.isArray(row)) continue;
            const rowVals = row.map((v) =>
              v === undefined || v === null ? "" : v.toString().trim().toUpperCase()
            );
            if (
              rowVals.some((v) => v === "SN") &&
              rowVals.some((v) => v === "MNO")
            ) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            throw new Error("Header row with SN and MNo not found");
          }

          const dataRows = rows.slice(headerRowIndex + 1);

          // Locate MNo column
          const headerRow = rows[headerRowIndex] || [];
          let mnoColIndex = headerRow.findIndex((c) =>
            c && c.toString().toUpperCase().includes("MNO")
          );
          if (mnoColIndex === -1 && headerRow.length > 1) {
            mnoColIndex = 1; // fallback to second column
          }
          if (mnoColIndex === -1) {
            throw new Error("MNo column not found");
          }

          for (const row of dataRows) {
            if (!Array.isArray(row)) continue;
            const val = row[mnoColIndex];
            if (val === undefined || val === null) continue;
            const valStr = val.toString().trim();
            if (valStr === "") continue;
            const cleanId = valStr.replace(/\D/g, "");
            if (cleanId) {
              data.push(row);
              memberIdsList.push(parseInt(cleanId, 10));
            }
          }
        };

        if (file.name.endsWith(".csv")) {
          const text = e.target.result;
          const lines = text.split("\n");
          const rows = lines
            .map((line) =>
              line
                .split(",")
                .map((v) => v.trim().replace(/^"|"$/g, ""))
            )
            .filter((row) => Array.isArray(row));
          processRows(rows);
        } else {
          const binaryStr = e.target.result;
          const workbook = XLSX.read(binaryStr, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          processRows(sheetData);
        }

        const count = data.length;

        const seen = new Set();
        const uniqueMemberIds = [];
        for (const id of memberIdsList) {
          if (!seen.has(id)) {
            seen.add(id);
            uniqueMemberIds.push(id);
          }
        }

        setMemberCount(count);
        setMemberIds(uniqueMemberIds);
        setLoading(false);
        setOpen(true);
        setDialogType("success");
      } catch (error) {
        setFileError("Error reading file. Please ensure the file is valid.");
        setIsFileUploaded(false);
        setUploadedFile(null);
        setMemberCount(0);
        setLoading(false);
        setDialogType("error");
      }
    };

    reader.onerror = () => {
      setFileError("Error reading file. Please try again.");
      setIsFileUploaded(false);
      setUploadedFile(null);
      setMemberCount(0);
      setLoading(false);
    };

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const handleClearFile = () => {
    setUploadedFile(null);
    setIsFileUploaded(false);
    setFileError("");
    setNarration("");
    setBank("");
    setChequeno("");
    setDeductionAmount(13);
    setTransdate(null);
    setDeductFrom("Subscription");
    setMemberCount(0);
    setMemberIds([]);
    setLoading(false);
    setDrawerOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (
      !uploadedFile ||
      !narration ||
      !bank ||
      !chequeno ||
      !deductionAmount ||
      !transdate ||
      !deductFrom
    ) {
      setSubmitError("Please fill all required fields");
      return;
    }

    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("narration", narration);
      formData.append("bank", bank);
      formData.append("chequeno", chequeno);
      formData.append("deduction_amount", deductionAmount);
      formData.append("transtype", deductFrom);

      let dateStr;
      if (transdate && typeof transdate.format === "function") {
        dateStr = transdate.format("YYYY-MM-DD");
      } else if (transdate) {
        const parsedDate = dayjs(transdate);
        dateStr = parsedDate.isValid()
          ? parsedDate.format("YYYY-MM-DD")
          : transdate;
      } else {
        setSubmitError("Invalid transaction date");
        setSubmitting(false);
        return;
      }
      formData.append("transdate", dateStr);
      formData.append(
        "createdby",
        localStorage.getItem("membername") || "username"
      );

      const response = await CustomAxios.post(
        "oasis/process_custom_deduction/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setSubmitSuccess(true);
        const successfulDeductions =
          response.data.deduction_results?.successful_deductions || [];
        const skippedDeductions = [
          ...(response.data.deduction_results?.insufficient_balance || []),
          ...(response.data.deduction_results?.failed || []),
        ];

        navigate("/custom-deduction-results", {
          state: {
            deductionResults: successfulDeductions,
            skippedDeductions: skippedDeductions,
            deductionStatus: "success",
          },
        });
      } else {
        setSubmitError(response.data.error || "Failed to process deduction");
      }
    } catch (error) {
      setSubmitError(
        error.response?.data?.error ||
          "Error processing deduction. Please try again."
      );
    }
  };
  const handleConfirmDeduction = async () => {
    setSubmitting(true);
    try {
      await handleSubmit(); // your API call
      setOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Back Button */}
      <Dialog
        open={open}
        PaperProps={{
          style: {
            width: "521px",
            boxShadow: "0px 0px 34px 0px rgba(100, 116, 139, 0.50)",
            background: "#F8FAFC",
            borderRadius: "0px",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "inter",
            fontSize: "18px",
            fontWeight: "600",
            lineHeight: "28px",
            color: "#242424",
          }}
        >
          {dialogType === "success"
            ? "File uploaded successfully"
            : "Confirm Deduction"}
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              alignItems: "start",
            }}
          >
            {dialogType === "success" && (
              <>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Filename: {uploadedFile?.name && `(${uploadedFile.name})`}
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Member Count: {memberCount}
                </Typography>
              </>
            )}

            {dialogType === "confirm" && (
              <Box>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Are you sure you want to process deduction for {memberCount} members?
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Deduct From: <strong>{deductFrom}</strong>
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Trans Date: <strong>{transdate ? transdate.format("DD-MM-YYYY") : "-"}</strong>
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                    Bank: <strong>{bank}</strong>
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Cheque No: <strong>{chequeno}</strong>
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Deduction Amount: <strong>{deductionAmount}</strong>
                </Typography>
                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "28px", color: "#242424" }}>
                  Narration: <strong>{narration}</strong>
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignSelf: "center",
                marginTop: "10px",
              }}
            >
              {dialogType === "confirm" && (
                <Button
                  variant="outlined"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                onClick={
                  dialogType === "success"
                    ? () => setOpen(false)
                    : handleConfirmDeduction
                }
                disabled={submitting}
                sx={{
                  background: "#288d2c",
                  textTransform: "none",
                  fontFamily: "inter",
                  fontSize: "16px",
                  fontWeight: "400",
                  width: "120px",
                  "&:hover": { background: "#1f6d22" },
                }}
              >
                {submitting ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : dialogType === "success" ? (
                  "Ok"
                ) : (
                  "Confirm"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          color: "#64748B",
          fontFamily: "inter",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: "#F1F5F9",
          },
        }}
      >
        Back to Deduction Records
      </Button>

      {/* File Upload Section */}
      {!isFileUploaded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "#CBD5E1",
              p: 4,
              textAlign: "center",
              bgcolor: "#FFFFFF",
              cursor: "pointer",
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                borderColor: "#94A3B8",
              },
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              ref={fileInputRef}
              onChange={handleFileInput}
            />
            <Stack spacing={2} alignItems="center">
              <CloudUploadOutlinedIcon
                sx={{ fontSize: 48, color: "#94A3B8" }}
              />
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                Drag & drop Excel or CSV file here or
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  variant="text"
                  sx={{ textTransform: "none" }}
                >
                  browse
                </Button>
              </Typography>
              <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                Accepts Excel (.xlsx, .xls) or CSV (.csv) files. Maximum file
                size: 10MB
              </Typography>
            </Stack>
          </Paper>

          {fileError && (
            <Typography sx={{ color: "red", fontSize: 14, fontWeight: 400 }}>
              {fileError}
            </Typography>
          )}
        </Box>
      )}

      {/* Member Count Display */}
      {(isFileUploaded || loading) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "12px 16px",
            backgroundColor: "#F1F5F9",
            borderRadius: "4px",
            border: "1px solid #CBD5E1",
          }}
        >
          {loading ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                py: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#288d2c" }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#64748B",
                  fontFamily: "inter",
                }}
              >
                Processing file...
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Typography
                    sx={{
                      fontFamily: "inter",
                      fontSize: "16px",
                      fontWeight: "600",
                      lineHeight: "24px",
                      color: "#242424",
                    }}
                  >
                    Member Count:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inter",
                      fontSize: "18px",
                      fontWeight: "700",
                      lineHeight: "28px",
                      color: "#288d2c",
                    }}
                  >
                    {memberCount}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "inter",
                      fontSize: "14px",
                      fontWeight: "400",
                      lineHeight: "20px",
                      color: "#64748B",
                      ml: 1,
                    }}
                  >
                    {uploadedFile?.name && `(${uploadedFile.name})`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                      textTransform: "none",
                      fontFamily: "inter",
                      fontSize: "14px",
                      color: "#288d2c",
                      borderColor: "#288d2c",
                      "&:hover": {
                        borderColor: "#1f6d22",
                        backgroundColor: "#F0F9F0",
                      },
                    }}
                  >
                    View Members
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFile}
                    sx={{
                      textTransform: "none",
                      fontFamily: "inter",
                      fontSize: "14px",
                      color: "#64748B",
                      borderColor: "#CBD5E1",
                      "&:hover": {
                        borderColor: "#94A3B8",
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >
                    Remove File
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Form Fields Section */}
      {isFileUploaded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
                      mt: 2,
             height:"600px" 
          }}
        >
          <Typography
            sx={{
              fontFamily: "inter",
              fontSize: "18px",
              fontWeight: "600",
              lineHeight: "28px",
              color: "#242424",
            }}
          >
            Deduction Details
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px"}}>
            {/* First Row: Deduct From and Trans Date */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MuiAutocomplete
                  required={true}
                  value={deductFrom || "Subscription"}
                  onChange={(event, newValue) => setDeductFrom(newValue)}
                  autoCompleteStyle={{
                    width: "100%",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  options={["Share", "Subscription"].sort()}
                  label="Deduct From *"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiDatePicker
                  label="Trans Date"
                  required
                  value={transdate}
                  onChange={(e) => setTransdate(e)}
                  disableFuture
                  format="DD-MM-YYYY"
                  sx={{ backgroundColor: "white", width: "100%" }}
                />
              </Grid>
            </Grid>

            {/* Second Row: Bank and Cheque No */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                              <MuiAutocomplete required={true} value={bank || ''} onChange={(event, newValue) => setBank(newValue)} autoCompleteStyle={{ width: "100%", height: "36px", display: "flex", alignItems: "center", zIndex: "1000" }} options={bankList.sort()} label="Bank *" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiInput
                  type="text"
                  label="Cheque No"
                  required
                  variant="outlined"
                  value={chequeno}
                  onChange={(e) => setChequeno(e.target.value)}
                  sx={{
                    width: "100%",
                    backgroundColor: "#FFF",
                    fontSize: "14px",
                    color: "#101010",
                  }}
                />
              </Grid>
            </Grid>

            {/* Third Row: Deduction Amount and Narration */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MuiInputAmount
                  inputKey="custom_deduction_amount"
                  ref={deductionAmountRef}
                  amount={deductionAmount}
                  setAmount={setDeductionAmount}
                  label="Deduction Amount"
                  required
                  sx={{
                    width: "100%",
                    backgroundColor: "#FFF",
                    fontSize: "14px",
                    color: "#101010",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiInput
                  type="text"
                  label="Narration"
                  required
                  variant="outlined"
                  value={narration || ""}
                  onChange={(e) => setNarration(e.target.value)}
                  sx={{
                    width: "100%",
                    backgroundColor: "#FFF",
                    fontSize: "14px",
                    color: "#101010",
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Submit Button */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                textTransform: "none",
                fontFamily: "inter",
                fontSize: "16px",
                fontWeight: "400",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpen(true);
                setDialogType("confirm");
              }}
              disabled={
                submitting ||
                !narration ||
                !bank ||
                !chequeno ||
                !deductionAmount ||
                !transdate ||
                !deductFrom ||
                !uploadedFile
              }
              sx={{
                background: "#288d2c",
                textTransform: "none",
                fontFamily: "inter",
                fontSize: "16px",
                fontWeight: "400",
                "&:hover": {
                  background: "#1f6d22",
                },
              }}
            >
                Process Deduction Withdrawal
            </Button>
          </Box>

          {submitError && (
            <Typography
              sx={{ color: "red", fontSize: 14, fontWeight: 400, mt: 1 }}
            >
              {submitError}
            </Typography>
          )}

          {submitSuccess && (
            <Typography
              sx={{ color: "#288d2c", fontSize: 14, fontWeight: 400, mt: 1 }}
            >
              Deduction processed successfully! Redirecting...
            </Typography>
          )}
        </Box>
      )}

      {/* Members Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSearchMemberId("");
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "500px", md: "600px" },
            height: "calc(100vh - 40px)",
            marginTop: "40px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "20px",
            overflow: "hidden",
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "16px",
              borderBottom: "1px solid #CBD5E1",
              flexShrink: 0,
              gap: "16px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "20px",
                fontWeight: "600",
                lineHeight: "28px",
                color: "#242424",
              }}
            >
              Member List
            </Typography>
            <Box sx={{ flex: 1, maxWidth: "300px" }}>
              <TextField
                size="small"
                placeholder="Search Member ID..."
                value={searchMemberId}
                onChange={(e) => setSearchMemberId(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: "18px", color: "#64748B" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "100%",
                  fontSize: "12px",
                  fontFamily: "inter",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "12px",
                    height: "36px",
                    backgroundColor: "#FFFFFF",
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
                setSearchMemberId("");
              }}
              sx={{
                color: "#64748B",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Member Count Summary */}
          <Box
            sx={{
              marginBottom: "16px",
              padding: "12px 16px",
              backgroundColor: "#F1F5F9",
              borderRadius: "4px",
              flexShrink: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "14px",
                fontWeight: "500",
                color: "#64748B",
              }}
            >
              Total Members:{" "}
              <span
                style={{ color: "#288d2c", fontWeight: 600 }}
              >{`${memberIds.length}`}</span>
              {searchMemberId && (
                <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                  (Filtered:{" "}
                  {
                    memberIds.filter((id) =>
                      id.toString().includes(searchMemberId)
                    ).length
                  }
                  )
                </span>
              )}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontFamily: "inter",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#64748B",
                  whiteSpace: "nowrap",
                }}
              >
                Rows per page:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(e.target.value)}
                  sx={{
                    fontSize: "12px",
                    height: "28px",
                    fontFamily: "inter",
                    "& .MuiSelect-select": {
                      padding: "4px 8px",
                    },
                  }}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Members Table */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              border: "1px solid #CBD5E1",
              borderRadius: "4px",
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                "& .p-datatable": {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                },
                "& .p-datatable-wrapper": {
                  flex: 1,
                  overflow: "auto",
                },
                "& .p-paginator": {
                  flexShrink: 0,
                  padding: "8px",
                  fontSize: "11px",
                  position: "relative",
                  overflow: "visible",
                },
              }}
            >
              <DataTable
                value={(() => {
                  const filtered = memberIds.filter(
                    (memberId) =>
                      !searchMemberId ||
                      memberId.toString().includes(searchMemberId)
                  );
                  return filtered.map((memberId, index) => ({
                    sno: index + 1,
                    memberId: memberId,
                  }));
                })()}
                scrollable
                scrollHeight="flex"
                paginator
                rows={rowsPerPage}
                rowsPerPageOptions={[]}
                paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                emptyMessage="No members found"
                style={{
                  fontSize: "11px",
                  fontFamily: "inter",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                className="p-datatable-sm"
              >
                <Column
                  field="sno"
                  header="S.No"
                  style={{ width: "70px", textAlign: "left" }}
                  headerStyle={{
                    fontSize: "11px",
                    fontWeight: "600",
                    padding: "4px 8px",
                    backgroundColor: "#F8FAFC",
                  }}
                  bodyStyle={{
                    fontSize: "11px",
                    padding: "2px 8px",
                  }}
                />
                <Column
                  field="memberId"
                  header="Member ID"
                  style={{ width: "120px" }}
                  headerStyle={{
                    fontSize: "11px",
                    fontWeight: "600",
                    padding: "4px 8px",
                    backgroundColor: "#F8FAFC",
                  }}
                  bodyStyle={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    fontWeight: "500",
                  }}
                />
              </DataTable>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}


